(() => {
  const text = (element) => (element?.textContent || "").replace(/\s+/g, " ").trim();
  const labelDialog = (dialog) => {
    if (dialog.hasAttribute("aria-label") || dialog.hasAttribute("aria-labelledby")) return;
    const heading = dialog.querySelector("h1, h2, h3, [data-fancybox-title]");
    const label = text(heading)
      || (dialog.classList.contains("fancybox-container--filter") ? "Product filters" : "Dialog window");
    dialog.setAttribute("aria-label", label);
  };
  let uniqueIdCounter = 0;
  const matching = (root, selector) => [
    ...(root.matches?.(selector) ? [root] : []),
    ...(root.querySelectorAll?.(selector) || []),
  ];
  const isExposed = (element) => {
    if (!element || element.closest("[hidden], [aria-hidden='true']")) return false;
    const style = getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return style.display !== "none"
      && style.visibility !== "hidden"
      && Number(style.opacity) !== 0
      && (rect.width !== 0 || rect.height !== 0);
  };
  const replaceHeading = (heading, level) => {
    const replacement = document.createElement(`h${level}`);
    for (const attribute of heading.attributes) replacement.setAttribute(attribute.name, attribute.value);
    while (heading.firstChild) replacement.append(heading.firstChild);
    heading.replaceWith(replacement);
    return replacement;
  };
  const uniquifyIds = (root) => {
    matching(root, "[id]").forEach((element) => {
      const oldId = element.id;
      if (!oldId || document.querySelectorAll(`[id="${CSS.escape(oldId)}"]`).length < 2) return;
      const newId = `${oldId}-ada-${++uniqueIdCounter}`;
      element.id = newId;
      matching(root, `label[for="${CSS.escape(oldId)}"]`).forEach((label) => label.htmlFor = newId);
      for (const attribute of ["aria-controls", "aria-describedby", "aria-labelledby", "aria-owns"]) {
        matching(root, `[${attribute}~="${CSS.escape(oldId)}"]`).forEach((owner) => {
          owner.setAttribute(attribute, owner.getAttribute(attribute).split(/\s+/).map((id) => id === oldId ? newId : id).join(" "));
        });
      }
    });
  };
  const normalizeHeadingJumps = (root) => {
    let previous = 0;
    document.querySelectorAll("h1, h2, h3, h4, h5, h6, [role='heading'][aria-level]").forEach((heading) => {
      if (!isExposed(heading)) return;
      const nativeLevel = Number(heading.tagName?.slice(1));
      const level = Number(heading.getAttribute("aria-level")) || nativeLevel;
      if (previous && level > previous + 1 && nativeLevel) {
        replaceHeading(heading, previous + 1);
        previous += 1;
      } else previous = level;
    });
  };
  const ensurePageHeading = () => {
    const main = document.querySelector("#pageContent[role='main']");
    if (!main || main.querySelector("h1")) return;
    const candidate = [...main.querySelectorAll("h2.h1-style")].find(isExposed);
    if (candidate) replaceHeading(candidate, 1);
  };
  const setAttribute = (element, name, value) => {
    if (element && element.getAttribute(name) !== value) element.setAttribute(name, value);
  };
  const managedSurfaces = new Map();
  let lastDialogTrigger = null;
  const disclosureSelector = "filter-toggle, a.filter-toggle, menu-popup > a.dropdn-link, a.prd-icon";
  const focusableSelector = [
    "button:not([disabled])",
    "a[href]",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
  ].join(",");
  const replaceCheckoutHeading = (root) => {
    matching(root, "h2.line-text").forEach((heading) => {
      if (text(heading).toLowerCase() !== "guaranteed safe checkout") return;
      const replacement = document.createElement("div");
      for (const attribute of heading.attributes) replacement.setAttribute(attribute.name, attribute.value);
      while (heading.firstChild) replacement.append(heading.firstChild);
      heading.replaceWith(replacement);
    });
  };
  const syncDisclosureState = (root = document) => {
    matching(root, "filter-toggle").forEach((trigger) => {
      setAttribute(trigger, "role", "button");
      if (trigger.tabIndex < 0) trigger.tabIndex = 0;
      setAttribute(trigger, "aria-controls", "filterColumn");
      setAttribute(trigger, "aria-expanded", String(trigger.classList.contains("is-opened")));
    });
    matching(root, "a.filter-toggle").forEach((trigger) => {
      setAttribute(trigger, "role", "button");
      setAttribute(trigger, "aria-controls", "dropdnFilterPopup");
      const open = Boolean(document.querySelector(".fancybox-container--filter.fancybox-is-open"));
      setAttribute(trigger, "aria-expanded", String(open));
    });
    matching(root, "menu-popup > a.dropdn-link").forEach((trigger) => {
      setAttribute(trigger, "aria-label", "Menu");
      setAttribute(trigger, "role", "button");
      setAttribute(trigger, "aria-haspopup", "dialog");
      setAttribute(trigger, "aria-controls", "dropdnMenuTabPopup");
      setAttribute(trigger, "aria-expanded", String(Boolean(document.querySelector("#dropdnMenuTabPopup.is-opened"))));
    });
    matching(root, "a.prd-icon").forEach((trigger) => setAttribute(trigger, "aria-haspopup", "dialog"));
  };
  const isManagedSurfaceOpen = (surface) => surface.isConnected && (
    surface.matches("#dropdnMenuTabPopup.is-opened")
    || surface.matches(".fancybox-container.fancybox-is-open")
  );
  const managedSurfaceKind = (surface, opener) => {
    if (surface.id === "dropdnMenuTabPopup") return "navigation";
    if (surface.classList.contains("fancybox-container--filter")) return "filters";
    if (opener?.matches("a.prd-icon")) return "quick-view";
    return null;
  };
  const surfaceFocusables = (surface) => [...surface.querySelectorAll(focusableSelector)].filter(isExposed);
  const describeOpener = (opener) => opener ? {
    controls: opener.getAttribute("aria-controls"),
    label: opener.getAttribute("aria-label"),
    href: opener.getAttribute("href"),
    className: opener.className,
  } : null;
  const resolveOpener = (state) => {
    if (state.opener?.isConnected && isExposed(state.opener)) return state.opener;
    const descriptor = state.openerDescriptor;
    if (!descriptor) return null;
    const candidates = descriptor.controls
      ? [...document.querySelectorAll(`[aria-controls="${CSS.escape(descriptor.controls)}"]`)]
      : descriptor.label
        ? [...document.querySelectorAll(`[aria-label="${CSS.escape(descriptor.label)}"]`)]
        : [];
    return candidates.find(isExposed) || null;
  };
  const restoreSurfaceOpener = (state) => {
    const opener = resolveOpener(state);
    if (!opener) return false;
    setAttribute(opener, "aria-expanded", "false");
    const active = document.activeElement;
    if (!active || active === document.body || active === document.documentElement || active === state.opener) {
      opener.focus({ preventScroll: true });
    }
    return document.activeElement === opener;
  };
  const configureSurface = (surface, opener) => {
    const kind = managedSurfaceKind(surface, opener);
    if (!kind) return false;
    setAttribute(surface, "role", "dialog");
    setAttribute(surface, "aria-modal", "true");
    const heading = surface.querySelector(".prd-block_title, .dropdn-modal-slide-title.is-current, h1, h2, h3");
    const label = kind === "navigation" ? "Navigation menu"
      : kind === "filters" ? "Product filters"
      : text(heading) || "Quick view";
    surface.removeAttribute("aria-labelledby");
    setAttribute(surface, "aria-label", label);
    const close = surface.querySelector("[data-fancybox-close], button.fancybox-close-small");
    if (close) setAttribute(close, "aria-label", kind === "navigation" ? "Close navigation menu" : kind === "filters" ? "Close product filters" : "Close quick view");
    if (opener) setAttribute(opener, "aria-expanded", "true");
    managedSurfaces.set(surface, { opener, openerDescriptor: describeOpener(opener), kind });
    for (const delay of [80, 400, 1000, 1600]) window.setTimeout(() => {
      if (!isManagedSurfaceOpen(surface)) return;
      const currentHeading = surface.querySelector(".prd-block_title, .dropdn-modal-slide-title.is-current, h1, h2, h3");
      const currentClose = surface.querySelector("[data-fancybox-close], button.fancybox-close-small");
      if (kind === "quick-view" && text(currentHeading)) setAttribute(surface, "aria-label", text(currentHeading));
      if (currentClose) setAttribute(currentClose, "aria-label", kind === "navigation" ? "Close navigation menu" : kind === "filters" ? "Close product filters" : "Close quick view");
      const target = kind === "quick-view"
        ? currentHeading || currentClose || surfaceFocusables(surface)[0]
        : currentClose || currentHeading || surfaceFocusables(surface)[0];
      if (target) {
        if (!target.matches(focusableSelector)) target.tabIndex = -1;
        target.focus({ preventScroll: true });
      }
    }, delay);
    return true;
  };
  const syncManagedSurfaces = () => {
    document.querySelectorAll("#dropdnMenuTabPopup.is-opened, .fancybox-container.fancybox-is-open[role='dialog']").forEach((surface) => {
      if (!managedSurfaces.has(surface) && configureSurface(surface, lastDialogTrigger)) lastDialogTrigger = null;
    });
    for (const [surface, state] of managedSurfaces) {
      if (isManagedSurfaceOpen(surface)) continue;
      managedSurfaces.delete(surface);
      restoreSurfaceOpener(state);
    }
    syncDisclosureState();
  };
  const activeManagedSurface = () => [...managedSurfaces.keys()].reverse().find(isManagedSurfaceOpen);
  const closeManagedSurface = (surface) => {
    const state = managedSurfaces.get(surface);
    const close = surface.querySelector("[data-fancybox-close], button.fancybox-close-small");
    if (close) close.click();
    for (const delay of [80, 250, 700]) window.setTimeout(() => {
      syncManagedSurfaces();
      if (!state || isManagedSurfaceOpen(surface) || activeManagedSurface()) return;
      restoreSurfaceOpener(state);
    }, delay);
  };
  const installManagedInteractionHandlers = () => {
    document.addEventListener("click", (event) => {
      const trigger = event.target.closest?.(disclosureSelector);
      if (!trigger) return;
      lastDialogTrigger = trigger;
      window.setTimeout(syncManagedSurfaces, 0);
      window.setTimeout(syncManagedSurfaces, 120);
    }, true);
    document.addEventListener("keydown", (event) => {
      const trigger = event.target.closest?.(disclosureSelector);
      if (trigger && (event.key === " " || (trigger.matches("filter-toggle") && event.key === "Enter"))) {
        event.preventDefault();
        trigger.click();
        return;
      }
      const surface = activeManagedSurface();
      if (!surface) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        closeManagedSurface(surface);
        return;
      }
      if (event.key !== "Tab") return;
      const focusables = surfaceFocusables(surface);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && (document.activeElement === first || !surface.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !surface.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    }, true);
  };
  const remediate = (root = document) => {
    uniquifyIds(root);
    matching(root, ".fancybox-container[role='dialog']").forEach(labelDialog);
    matching(root, "button.fancybox-close-small:not([aria-label])").forEach((button) => button.setAttribute("aria-label", "Close dialog"));
    matching(root, "a.btn-link-wishlist:not([aria-label])").forEach((link) => {
      link.setAttribute("aria-label", link.dataset.tippyContent || window.themeStrings?.addToWishlist || "Add to wishlist");
    });
    matching(root, "a.drop-fixed-toggle-wrap:not([aria-label])").forEach((link) => link.setAttribute("aria-label", "Show cart details"));
    matching(root, "form.hdr-open-search-form[role='search']:not([aria-label])").forEach((form) => form.setAttribute("aria-label", "Header product search"));
    matching(root, "header search-page input[type='search']:not([aria-label])").forEach((field) => field.setAttribute("aria-label", "Search products"));
    // The spam-protection notice is useful page information outside the form.
    matching(root, "#shop-hcaptcha-badge-container:not([role])").forEach((region) => {
      region.setAttribute("role", "region");
      if (!region.hasAttribute("aria-label") && !region.hasAttribute("aria-labelledby")) region.setAttribute("aria-label", "Spam protection information");
    });
    // This is the title/actions group of a dialog, rather than a second page banner.
    matching(root, "#shopify-pc__prefs__dialog[role='dialog'] > header:not([role])").forEach((header) => header.setAttribute("role", "none"));
    matching(root, "textarea[id^='h-captcha-response-']:not([aria-label])").forEach((field) => field.setAttribute("aria-label", "hCaptcha response"));
    matching(root, "textarea[name='g-recaptcha-response']:not([aria-label])").forEach((field) => field.setAttribute("aria-label", "reCAPTCHA response"));
    matching(root, "#pc--browser-opt-out-checkbox:not([aria-label])").forEach((field) => field.setAttribute("aria-label", "Browser opt-out status"));
    matching(root, ".post-prw[aria-labelledby]").forEach((card) => {
      const labelId = card.getAttribute("aria-labelledby");
      if (!labelId || document.getElementById(labelId)) return;
      const heading = card.querySelector(".post-prw-title");
      if (heading) heading.id = labelId;
    });
    matching(root, ".table-responsive").forEach((region) => {
      if (!region.querySelector("table")) return;
      if (region.scrollWidth > region.clientWidth || region.scrollHeight > region.clientHeight) region.tabIndex = 0;
      region.querySelectorAll("th").forEach((header) => {
        if (text(header)) return;
        const label = document.createElement("span");
        label.className = "visually-hidden";
        label.textContent = "Comparison factor";
        header.replaceChildren(label);
      });
    });
    matching(root, "[data-tippy-root]:not([role])").forEach((region) => { region.setAttribute("role", "region"); region.setAttribute("aria-label", "Tooltip"); });
    replaceCheckoutHeading(root);
    syncDisclosureState(root);
    ensurePageHeading();
    normalizeHeadingJumps(root);
  };
  const start = () => {
    remediate();
    installManagedInteractionHandlers();
    syncManagedSurfaces();
    new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) if (node.nodeType === 1) remediate(node);
      }
      syncManagedSurfaces();
    }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "aria-hidden"] });
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start, { once: true });
  else start();
})();
